import { useAuth } from "@clerk/clerk-react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrganizationProProfileStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { fetcherAuth } from "utils/api";

import {
  DefaultProOnboardingStepInfo,
  DefaultProOnboardingSteps,
  ProOnboardingStep,
} from "../schema";
import styles from "./styles";

const ProOnboardingProgress = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const [resumeLoading, setResumeLoading] = useState(false);
  const navigate = useNavigate();

  const { organizationProProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const isComplete = (index: number) => {
    if (isInMiddleOfDifferentOnboarding()) return false;
    return (
      index < (organizationProProfile?.onboardingState?.currentStepIndex || 0)
    );
  };

  const calculatePercentageOfCompletion = () => {
    const currentStepIndex =
      organizationProProfile?.onboardingState?.currentStepIndex || 0;
    const totalSteps = DefaultProOnboardingSteps.length;
    return Math.round((currentStepIndex / totalSteps) * 100);
  };

  // Edge case: user in the middle of an old onboarding flow but never complete
  const isInMiddleOfDifferentOnboarding = () => {
    if (!organizationProProfile?.onboardingState) return false;
    return !lodash.isEqual(
      organizationProProfile.onboardingState.steps,
      DefaultProOnboardingSteps,
    );
  };

  const handleProceed = async () => {
    setResumeLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/pro-status`,
        "PATCH",
        {},
        {
          status: OrganizationProProfileStatus.pending,
        },
      );
      navigate(`/${routes.onboardingPro}`);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setResumeLoading(false);
    }
  };

  return (
    <Box
      sx={{
        py: 3,
        borderRadius: 4,
      }}
    >
      <Typography
        variant="h2"
        sx={{
          fontSize: { xs: 30, md: 40 },
          lineHeight: 1.3,
          maxWidth: 800,
        }}
      >
        Your Pro Setup is Incomplete 👀
      </Typography>

      <Divider sx={{ my: 3, backgroundColor: "purple.light" }} />

      <Grid container gap={2} alignItems={"center"}>
        <Grid item xs md>
          {isInMiddleOfDifferentOnboarding() ? (
            <Typography
              gutterBottom
              sx={{ fontSize: { xs: 14, md: 18, fontWeight: 600 } }}
            >
              👋 We have updated our onboarding process since your last login
            </Typography>
          ) : (
            <Typography
              sx={{
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: { xs: 14, md: 18 },
              }}
              gutterBottom
            >
              You're {calculatePercentageOfCompletion()}% set up!
            </Typography>
          )}

          <Typography sx={{ fontSize: 16 }}>
            {isInMiddleOfDifferentOnboarding()
              ? "But don't worry, we still store your old information. Click Proceed Onboarding Setup to resume."
              : "Pro Users who complete onboarding are more likely to land brand collaborations."}
          </Typography>
        </Grid>

        <Grid item xs={12} md="auto">
          <StyledLargeButton
            styles={{
              fontSize: { xs: 12, md: 16 },
              color: "purple.dark",
              border: `1px solid`,
              borderColor: "purple.dark",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "purple.light",
              },
            }}
            onClick={handleProceed}
            loading={resumeLoading}
          >
            Complete Onboarding Setup
          </StyledLargeButton>
        </Grid>
      </Grid>

      <Grid item xs={12} container gap={1} sx={{ mt: 3 }}>
        {DefaultProOnboardingSteps?.map((step: ProOnboardingStep, index) =>
          step in DefaultProOnboardingStepInfo ? (
            <Box
              sx={
                isComplete(index) ? styles.completeChip : styles.incompleteChip
              }
            >
              {/* technically there are the 'Welcome' screen which we track, but won't display on this UI here */}
              {isComplete(index) ? (
                <Box sx={styles.circle}>
                  <Box
                    component="i"
                    className="fa-regular fa-check"
                    sx={{ color: "white" }}
                  />
                </Box>
              ) : (
                <></>
              )}
              {DefaultProOnboardingStepInfo[step]?.name}

              {isComplete(index) ? (
                ""
              ) : (
                <Typography sx={styles.timeText}>
                  ⏱️{" "}
                  {
                    DefaultProOnboardingStepInfo[step as ProOnboardingStep]
                      ?.time
                  }{" "}
                  min
                </Typography>
              )}
            </Box>
          ) : null,
        )}
      </Grid>
    </Box>
  );
};

export default ProOnboardingProgress;
